import { TYPE } from '@kontentino/kontentino-constants/Pages';
import type { ContentPost } from 'api/new-analytics/types';
import {
  buildReportPreviewPagesForCard,
  reportPreviewPageHasContent,
  type ReportPreviewPageModel,
} from './buildReportPreviewPages';
import {
  FACEBOOK_REACTION_METRIC_KEYS,
  INSTAGRAM_INTERACTION_METRIC_KEYS,
  shouldShowFacebookReactionsCard,
  shouldShowInstagramInteractionsCard,
} from '../types/ReportPlatformBlock';
import type { ReportPlatformBlock } from '../types/ReportPlatformBlock';

function getPlatformBlockConsumedPerformanceKeys(
  cardType: 'general' | 'profile',
  pageType?: number,
): Set<string> {
  if (cardType === 'general' || pageType == null) {
    return new Set();
  }

  switch (pageType) {
    case TYPE.FACEBOOK:
    case TYPE.FACEBOOK_AD:
      return new Set([...FACEBOOK_REACTION_METRIC_KEYS, 'reactions']);
    case TYPE.INSTAGRAM:
      return new Set(INSTAGRAM_INTERACTION_METRIC_KEYS);
    default:
      return new Set();
  }
}

interface BuildPlatformReportPreviewPagesInput {
  pageType?: number;
  cardType: 'general' | 'profile';
  chartMetrics: string[];
  performanceTileMetrics: string[];
  performanceMetrics: string[];
  contentMetrics: string[];
  posts?: ContentPost[];
  includePosts?: boolean;
  performanceNote?: string | null;
  contentNote?: string | null;
}

function appendPlatformBlocksToFirstPerformancePage(
  pages: ReportPreviewPageModel[],
  blocks: ReportPlatformBlock[],
): ReportPreviewPageModel[] {
  if (blocks.length === 0 || pages.length === 0) {
    return pages;
  }

  const [firstPage, ...rest] = pages;
  const hasPerformanceContent =
    firstPage.chartMetrics.length > 0 ||
    firstPage.performanceTileMetrics.length > 0;

  if (!hasPerformanceContent) {
    return pages;
  }

  return [
    {
      ...firstPage,
      platformBlocks: [...(firstPage.platformBlocks ?? []), ...blocks],
    },
    ...rest,
  ];
}

export function buildPlatformReportPreviewPages(
  input: BuildPlatformReportPreviewPagesInput,
): ReportPreviewPageModel[] {
  const keepNoteOnlyFallback =
    Boolean(input.performanceNote?.trim()) ||
    Boolean(input.contentNote?.trim());
  const blockConsumedPerformanceKeys = getPlatformBlockConsumedPerformanceKeys(
    input.cardType,
    input.pageType,
  );
  const performanceTileMetrics = input.performanceTileMetrics.filter(
    (metric) => !blockConsumedPerformanceKeys.has(metric),
  );

  const metricPages = buildReportPreviewPagesForCard({
    chartMetrics: input.chartMetrics,
    performanceTileMetrics,
    contentMetrics: input.contentMetrics,
    posts: input.posts,
    includePosts: input.includePosts,
    performanceNote: input.performanceNote,
    contentNote: input.contentNote,
  });

  if (input.cardType === 'general' || input.pageType == null) {
    return metricPages;
  }

  let pages = metricPages;

  switch (input.pageType) {
    case TYPE.FACEBOOK:
    case TYPE.FACEBOOK_AD: {
      const performanceBlocks: ReportPlatformBlock[] = [];
      if (shouldShowFacebookReactionsCard(input.performanceMetrics)) {
        performanceBlocks.push({ kind: 'facebook_reactions' });
      }
      pages = appendPlatformBlocksToFirstPerformancePage(
        pages,
        performanceBlocks,
      );
      break;
    }
    case TYPE.INSTAGRAM: {
      const performanceBlocks: ReportPlatformBlock[] = [];
      if (shouldShowInstagramInteractionsCard(input.performanceMetrics)) {
        performanceBlocks.push({ kind: 'instagram_interactions' });
      }
      pages = appendPlatformBlocksToFirstPerformancePage(
        pages,
        performanceBlocks,
      );
      break;
    }
    default:
      break;
  }

  const filtered = pages.filter(reportPreviewPageHasContent);

  if (filtered.length === 0 && keepNoteOnlyFallback) {
    return metricPages;
  }

  return filtered;
}
